/**
 * Tech Support → Close out (1.5.0), the same shape as the crew's: the device(s) worked on, the problem as found,
 * what was done, what is recommended; the outcome — and when it is not fully resolved, why and what would
 * resolve it; a follow-up (what, by whom); the time finished (stamped, editable); the tenant present and able to
 * repeat the steps; and the boundary checklist — all three ticked to save. Not fully resolved goes to Needs your
 * attention for the office.
 */
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BOUNDARY_KEYS, HELP_KEYS, OUTCOMES, WHY_KEYS, type HelpKey, type TechCloseoutInput, type TechOutcome, type TechTicketView, type WhyKey } from '@shared/tech';
import { formatClock } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { PhotoPicker, type PhotoItem } from '@/features/finish/Photos';
import { uploadTechPhotos } from './techPhotos';
import { appendSteps, onWizardSteps, takePendingSteps } from './summary-steps';
import '@/styles/visit185.css';

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export function TechCloseoutSheet({ ticket, onDone, returnFocusTo, initialVisit }: { initialVisit?: { found: string; done: string; summary: string; resolved: boolean }; ticket: TechTicketView; onDone: () => void; returnFocusTo?: RefObject<HTMLElement | null> }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [devices, setDevices] = useState<HelpKey[]>(ticket.help);
  const [devicesOther, setDevicesOther] = useState(ticket.help.includes('other') ? ticket.other : '');
  const [found, setFound] = useState(initialVisit?.found ?? '');
  const [done, setDone] = useState(initialVisit?.done ?? '');
  const [next, setNext] = useState('');
  const [outcome, setOutcome] = useState<TechOutcome | null>(initialVisit?.resolved ? 'resolved' : null);
  const [why, setWhy] = useState<WhyKey | null>(null);
  const [whyOther, setWhyOther] = useState('');
  const [wouldResolve, setWouldResolve] = useState('');
  const [fuNeeded, setFuNeeded] = useState(false);
  const [fuWhat, setFuWhat] = useState('');
  const [fuWho, setFuWho] = useState('');
  const [finishedAt, setFinishedAt] = useState(hhmm(serviceMinutesNow()));
  const [present, setPresent] = useState<boolean | null>(null);
  const [repeat, setRepeat] = useState<boolean | null>(null);
  const [boundary, setBoundary] = useState({ noPasswordsKept: false, nothingInstalledWithoutConsent: false, settingsOnlyWithOk: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errRef = useRef<HTMLDivElement>(null);
  // 1.8.5 (D-1.8.5-5): the after-visit summary — what is saved is what is in this box when Save is pressed
  const [summary, setSummary] = useState(initialVisit?.summary ?? '');
  const [steps, setSteps] = useState('');
  const [draft, setDraft] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [afterPhotos, setAfterPhotos] = useState<PhotoItem[]>([]);
  const noteId = useId();
  const summaryRef = useRef<HTMLTextAreaElement>(null);

  // "Add these steps to my summary" (the Tech Wizard): steps sent before the sheet opened, then each new one
  useEffect(() => {
    const add = (text: string) => {
      setSteps((cur) => appendSteps(cur, text));
      setSummary((cur) => appendSteps(cur, text));
      announce(t('visit185.stepsAdded'));
    };
    for (const text of takePendingSteps(ticket.appointmentId)) add(text);
    return onWizardSteps(ticket.appointmentId, add);
  }, [ticket.appointmentId, announce, t]);

  const helpMeWrite = async () => {
    setDrafting(true);
    setDraftError(null);
    try {
      const r = await apiFetch<{ ok: true; draft: string }>(`/api/tech/tickets/${encodeURIComponent(ticket.appointmentId)}/summary-draft`, {
        method: 'POST',
        body: JSON.stringify({ found, done, next, steps }),
      });
      setDraft(r.draft);
      announce(t('visit185.draftReady'));
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      setDraftError(m);
      announce(m, 'assertive');
    } finally {
      setDrafting(false);
    }
  };

  const partial = outcome !== null && outcome !== 'resolved';
  const allBoundary = BOUNDARY_KEYS.every((k) => boundary[k]);
  const ready =
    devices.length > 0 &&
    (!devices.includes('other') || !!devicesOther.trim()) &&
    !!found.trim() &&
    !!done.trim() &&
    outcome !== null &&
    (!partial || (!!why && (why !== 'other' || !!whyOther.trim()) && !!wouldResolve.trim())) &&
    (!fuNeeded || !!fuWhat.trim()) &&
    present !== null &&
    repeat !== null &&
    allBoundary;

  const save = async () => {
    setBusy(true);
    setError(null);
    let afterPaths: string[] = [];
    try {
      afterPaths = await uploadTechPhotos(afterPhotos, ticket.appointmentId, 'after');
    } catch (e) {
      const m = (e as Error).message;
      setError(m);
      announce(m, 'assertive');
      setBusy(false);
      window.setTimeout(() => errRef.current?.focus(), 0);
      return;
    }
    const body: TechCloseoutInput = {
      devices,
      devicesOther: devicesOther.trim(),
      found: found.trim(),
      done: done.trim(),
      next: next.trim(),
      outcome: outcome!,
      why: partial ? why : null,
      whyOther: whyOther.trim(),
      wouldResolve: wouldResolve.trim(),
      followUp: { needed: fuNeeded, what: fuWhat.trim(), who: fuWho.trim() },
      finishedAt,
      tenantPresent: !!present,
      tenantCanRepeat: !!repeat,
      boundary,
      // words kept as written: only trailing space is trimmed
      summary: summary.replace(/\s+$/, ''),
      afterPhotos: afterPaths,
    };
    try {
      await apiFetch(`/api/tech/tickets/${encodeURIComponent(ticket.appointmentId)}/closeout`, { method: 'POST', body: JSON.stringify(body) });
      const m = partial ? t('techClose.savedFollowUp', { who: ticket.tenantFirst }) : t('techClose.saved', { who: ticket.tenantFirst });
      announce(m);
      void qc.invalidateQueries();
      onDone();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      setError(m);
      announce(m, 'assertive');
      window.setTimeout(() => errRef.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const yesNo = (name: string, v: boolean | null, set: (b: boolean) => void, label: string) => (
    <fieldset className="field">
      <legend>{label}</legend>
      <div className="seg radios">
        {[true, false].map((b) => (
          <label key={String(b)} className={v === b ? 'on' : ''}>
            <input type="radio" name={name} checked={v === b} onChange={() => set(b)} data-testid={`tc-${name}-${b ? 'yes' : 'no'}`} />
            {b ? t('techClose.yes') : t('techClose.no')}
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <SheetContent
      title={t('techClose.title', { who: ticket.tenantFirst, time: formatClock(ticket.minutes) })}
      returnFocusTo={returnFocusTo}
      wide
      data-testid="tech-closeout-sheet"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onDone}>
            {t('book.cancel')}
          </Button>
          <Button type="button" disabled={!ready || busy} onClick={() => save()} data-testid="tc-save">
            {busy ? t('book.sending') : t('techClose.save')}
          </Button>
        </>
      }
    >
      <fieldset className="field">
        <legend>{t('techClose.devices')}</legend>
        <div className="checks big">
          {HELP_KEYS.map((k) => (
            <label key={k} className="check">
              <input type="checkbox" checked={devices.includes(k)} onChange={(e) => setDevices(e.target.checked ? [...devices, k] : devices.filter((x) => x !== k))} data-testid={`tc-device-${k}`} />
              <span>{t(`techBook.help.${k}`)}</span>
            </label>
          ))}
        </div>
        {devices.includes('other') ? (
          <>
            <label htmlFor="tc-other">{t('techClose.devicesOther')}</label>
            <input id="tc-other" className="input" value={devicesOther} onChange={(e) => setDevicesOther(e.target.value)} placeholder={t('techClose.devicesOtherPlaceholder')} data-testid="tc-devices-other" />
          </>
        ) : null}
      </fieldset>
      {(
        [
          ['found', found, setFound, true],
          ['done', done, setDone, true],
          ['next', next, setNext, false],
        ] as const
      ).map(([k, v, set, req]) => (
        <div className="field" key={k}>
          <label htmlFor={`tc-${k}`}>{t(`techClose.${k}`)}</label>
          <textarea id={`tc-${k}`} className="input" value={v} onChange={(e) => set(e.target.value)} aria-required={req || undefined} data-testid={`tc-${k}`} />
        </div>
      ))}
      <section className="field v185-summary" aria-labelledby={`${noteId}-h`} data-testid="tc-summary-box">
        <label htmlFor={`${noteId}-summary`} id={`${noteId}-h`} className="v185-summary-h">
          {t('visit185.summaryTitle')}
        </label>
        <p className="v185-note" id={`${noteId}-note`} data-testid="tc-summary-note">
          {t('visit185.summaryNote')}
        </p>
        <textarea
          id={`${noteId}-summary`}
          ref={summaryRef}
          className="input"
          rows={6}
          maxLength={4000}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          aria-describedby={`${noteId}-note ${noteId}-help`}
          data-testid="tc-summary"
        />
        <p className="help" id={`${noteId}-help`}>
          {t('visit185.summaryHelp')}
        </p>
        <div className="row-actions">
          <Button type="button" variant="secondary" size="sm" disabled={drafting || !(found.trim() || done.trim() || next.trim() || steps.trim())} onClick={() => helpMeWrite()} data-testid="tc-help-write">
            <Icon name="sparkle" /> {drafting ? t('visit185.drafting') : t('visit185.helpWrite')}
          </Button>
        </div>
        {draftError ? (
          <div className="warn" role="alert" data-testid="tc-draft-error">
            <Icon name="alert" />
            <div>{draftError}</div>
          </div>
        ) : null}
        {draft !== null ? (
          <div className="v185-draft" role="region" aria-label={t('visit185.draftTitle')} data-testid="tc-draft">
            <b>{t('visit185.draftTitle')}</b>
            <p className="help">{t('visit185.draftHelp')}</p>
            <p className="v185-draft-text" data-testid="tc-draft-text">
              {draft}
            </p>
            <div className="row-actions">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setSummary(draft);
                  setDraft(null);
                  announce(t('visit185.draftUsed'));
                  window.setTimeout(() => summaryRef.current?.focus(), 0);
                }}
                data-testid="tc-draft-use"
              >
                {t('visit185.draftUse')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDraft(null);
                  announce(t('visit185.draftLeft'));
                  window.setTimeout(() => summaryRef.current?.focus(), 0);
                }}
                data-testid="tc-draft-leave"
              >
                {t('visit185.draftLeave')}
              </Button>
            </div>
          </div>
        ) : null}
      </section>
      <PhotoPicker id={`${noteId}-after`} items={afterPhotos} onChange={setAfterPhotos} legend={t('visit185.afterPhotos')} help={t('visit185.photosOptional')} pickedWords={(count) => t('finish.photoReadyWith', { count, button: t('techClose.save') })} testid="tc-after-photos" />
      <fieldset className="field">
        <legend>{t('techClose.outcome')}</legend>
        <div className="seg radios">
          {OUTCOMES.map((o) => (
            <label key={o} className={outcome === o ? 'on' : ''}>
              <input type="radio" name="tc-outcome" checked={outcome === o} onChange={() => setOutcome(o)} data-testid={`tc-outcome-${o}`} />
              {t(`techClose.o.${o}`)}
            </label>
          ))}
        </div>
      </fieldset>
      {partial ? (
        <>
          <div className="field">
            <label htmlFor="tc-why">{t('techClose.why')}</label>
            <select id="tc-why" className="input" value={why ?? ''} onChange={(e) => setWhy((e.target.value || null) as WhyKey | null)} data-testid="tc-why">
              <option value="">{t('techClose.choose')}</option>
              {WHY_KEYS.map((w) => (
                <option key={w} value={w}>
                  {t(`techClose.w.${w}`)}
                </option>
              ))}
            </select>
          </div>
          {why === 'other' ? (
            <div className="field">
              <label htmlFor="tc-why-other">{t('techClose.whyOther')}</label>
              <input id="tc-why-other" className="input" value={whyOther} onChange={(e) => setWhyOther(e.target.value)} data-testid="tc-why-other" />
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="tc-resolve">{t('techClose.wouldResolve')}</label>
            <textarea id="tc-resolve" className="input" value={wouldResolve} onChange={(e) => setWouldResolve(e.target.value)} aria-required data-testid="tc-would-resolve" />
          </div>
        </>
      ) : null}
      {yesNo('fu', fuNeeded, setFuNeeded, t('techClose.followUp'))}
      {fuNeeded ? (
        <div className="grid-2">
          <div className="field">
            <label htmlFor="tc-fu-what">{t('techClose.fuWhat')}</label>
            <input id="tc-fu-what" className="input" value={fuWhat} onChange={(e) => setFuWhat(e.target.value)} data-testid="tc-fu-what" />
          </div>
          <div className="field">
            <label htmlFor="tc-fu-who">{t('techClose.fuWho')}</label>
            <input id="tc-fu-who" className="input" value={fuWho} onChange={(e) => setFuWho(e.target.value)} data-testid="tc-fu-who" />
          </div>
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="tc-finished">{t('techClose.finishedAt')}</label>
        <input id="tc-finished" className="input" type="time" value={finishedAt} onChange={(e) => setFinishedAt(e.target.value)} data-testid="tc-finished" />
      </div>
      {yesNo('present', present, setPresent, t('techClose.present'))}
      {yesNo('repeat', repeat, setRepeat, t('techClose.repeat'))}
      <fieldset className="field boundary" data-testid="tc-boundary">
        <legend>{t('techClose.boundary')}</legend>
        {BOUNDARY_KEYS.map((k) => (
          <label key={k} className="check">
            <input type="checkbox" checked={boundary[k]} onChange={(e) => setBoundary({ ...boundary, [k]: e.target.checked })} data-testid={`tc-b-${k}`} />
            <span>{t(`techClose.b.${k}`)}</span>
          </label>
        ))}
        {!allBoundary ? <p className="help">{t('techClose.boundaryHelp')}</p> : null}
      </fieldset>
      {error ? (
        <div className="warn take" role="alert" tabIndex={-1} ref={errRef} data-testid="tc-error">
          <Icon name="alert" />
          <div>{error}</div>
        </div>
      ) : null}
    </SheetContent>
  );
}
